import {computed, ref} from "vue";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import {AnalysisClosingDocument, DocumentSender} from "@/medication-checkup/interfaces/analysis-closing";
import {InterventionsView} from "@/medication-checkup/interfaces/interventions";
import {closingDocumentFilename} from "@/medication-checkup/composables/useClosingDocument";
import {MSSantePrefill} from "@/mssante/interfaces/SecureMessaging";
import useCustomer from "@/customer/composables/useCustomer";

/**
 * The message sending the synthesis of the analysis to the attending doctor,
 * through the generic MSSanté dialog: the scene hands it a prefill and a
 * reference to the synthesis, the dialog does the rest.
 *
 * Held here because two scenes send it: the transmission to the doctor, and
 * the closing of the analysis on a cycle whose course does not hold the
 * transmission yet.
 */
export const useDoctorSynthesisMessage = (
    synthesis: () => AnalysisClosingDocument | null | undefined,
    conclusion: () => string | null | undefined,
    sender: () => DocumentSender | null | undefined
) => {
    const store = useMedicationCheckupStore();

    /**
     * The patient of the cycle, read through the shared cache the patient
     * column already fills: the dialog names them above its suggested
     * recipients and keys its favorites on them.
     */
    const {data: customer} = useCustomer(store.customerId ?? undefined);

    /**
     * Whether the MSSanté dialog is open.
     */
    const open = ref(false);

    /**
     * The patient as the doctor files them: surname in capitals, then first
     * name.
     */
    const patientName = computed((): string => {
        const information = customer.value?.information;
        const name = [information?.last_name?.toUpperCase(), information?.first_name]
            .filter(Boolean)
            .join(' ');

        return name || information?.name || 'votre patient';
    });

    /**
     * Whether the analysis proposed nothing to the doctor, read from the
     * interventions of the step: the letter then says the review found
     * nothing to change, instead of submitting proposals to their opinion.
     */
    const withoutProposal = computed((): boolean => {
        const block = store.displayedBlocks.find(candidate => candidate.definition_key === 'interventions');

        return (block?.view as InterventionsView | null | undefined)?.summary?.proposals === 0;
    });

    /**
     * The letter itself, the conclusion set in the middle of it when there
     * is one: an analysis may close without, and the message then stands on
     * the attachment alone.
     *
     * The message leaves from the single MSSanté mailbox of Apodis, so a
     * reply would never reach the pharmacy: the doctor is asked to send the
     * document back or to call on the contact the footer gives, never to
     * answer the message. The holder of the pharmacy signs it, the pharmacy
     * alone when it has none.
     */
    const body = (name: string): string => {
        const signed = conclusion()?.trim();
        const born = customer.value?.information?.formatted_birth_date;
        const signatory = sender();

        const context = `Dans le cadre du bilan partagé de médication de ${name}${born ? `, né(e) le ${born}` : ''}, `;

        return [
            'Cher Docteur,',
            '',
            withoutProposal.value
                ? context + "vous trouverez ci-joint la synthèse de l'analyse de ses traitements. "
                    + "Cette analyse ne relève aucune proposition d'intervention."
                : context + "vous trouverez ci-joint la synthèse de l'analyse de ses traitements et nos propositions "
                    + "d'intervention, soumises à votre avis.",
            ...(signed ? ['', signed] : []),
            '',
            'Vous pouvez nous retourner le document annoté ou nous contacter pour en échanger.',
            '',
            'Bien cordialement,',
            ...(signatory?.pharmacist ? [`${signatory.pharmacist}, pharmacien titulaire`] : []),
            ...(signatory?.pharmacy ? [signatory.pharmacy] : []),
        ].join('\n');
    };

    /**
     * The message proposed to the doctor: the propositions of the analysis,
     * with the synthesis attached. The dialog downloads it from the HDS
     * itself when the message becomes the active one, so the scene hands
     * over a reference rather than a blob.
     *
     * The conclusion the pharmacist signed carries the body: the doctor reads
     * the essential without opening the PDF. Everything here is a proposal —
     * the dialog lets them rewrite it all before sending.
     */
    const prefills = computed((): MSSantePrefill[] => {
        const document = synthesis();

        if (!document) {
            return [];
        }

        const name = patientName.value;

        return [{
            audience: 'professional',
            subject: `Bilan partagé de médication — ${name} — synthèse pharmaceutique`,
            body: body(name),
            documents: [{
                name: closingDocumentFilename(document, 'synthese-pharmaceutique'),
                url: document.url,
                source: 'hds',
            }],
        }];
    });

    return {customer, open, prefills};
};
